/**
 * recentSearchesSlice — search-UX §4.4 (FE).
 *
 * Persisted FIFO list of the user's recent search queries, surfaced
 * by `RecentSearchesDropdown` when the SearchBar has empty query +
 * focus.  Inspired by Google Photos / YouTube mobile / Pinterest —
 * the focus state of an empty input is prime real estate for
 * discoverability.
 *
 * Persistence strategy:
 *   - **Web:** `localStorage` under `founsi.recentSearches.v1`.  No
 *     extra dependencies, works on RN-Web today.
 *   - **Native:** session-only (in-memory Redux).  Native gets
 *     wiped on app restart.  Acceptable v1 trade-off — adding
 *     `@react-native-async-storage/async-storage` is a native-module
 *     dep + pod-install + bundle weight.  Promote to AsyncStorage
 *     only if native users complain.
 *
 * Cap at 8 entries; FIFO eviction; case-insensitive dedup on insert
 * (re-typing `wicker` after `wicker chair` moves it to the head, not
 * a duplicate row).
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../store/store';

const STORAGE_KEY = 'founsi.recentSearches.v1';
const MAX_RECENTS = 8;

interface RecentSearchesState {
  items: string[];
  hydrated: boolean;
}

const initialState: RecentSearchesState = {
  items: [],
  hydrated: false,
};

/** Pure helper — exported so the unit test can pin the semantics. */
export function applyAddRecent(
  current: string[],
  query: string,
  cap = MAX_RECENTS,
): string[] {
  const trimmed = (query ?? '').trim();
  if (!trimmed) return current;
  // Case-insensitive dedup; preserve the first-seen casing as the
  // canonical label.  Move to head if it already exists.
  const lower = trimmed.toLowerCase();
  const kept = current.filter((s) => s.toLowerCase() !== lower);
  return [trimmed, ...kept].slice(0, cap);
}

const slice = createSlice({
  name: 'recentSearches',
  initialState,
  reducers: {
    addRecent: (state, action: PayloadAction<string>) => {
      state.items = applyAddRecent(state.items, action.payload);
      persist(state.items);
    },
    removeRecent: (state, action: PayloadAction<string>) => {
      const lower = action.payload.toLowerCase();
      state.items = state.items.filter((s) => s.toLowerCase() !== lower);
      persist(state.items);
    },
    clearRecents: (state) => {
      state.items = [];
      persist([]);
    },
    /** Restore from storage.  Called once at app boot.  No-ops on
     *  native (storage I/O returns null). */
    hydrateRecents: (state, action: PayloadAction<string[]>) => {
      state.items = action.payload.slice(0, MAX_RECENTS);
      state.hydrated = true;
    },
  },
});

export const { addRecent, removeRecent, clearRecents, hydrateRecents } =
  slice.actions;
export default slice.reducer;

export const selectRecentSearches = (state: RootState): string[] =>
  state.recentSearches.items;

export const selectRecentsHydrated = (state: RootState): boolean =>
  state.recentSearches.hydrated;

/* ── Persistence helpers ───────────────────────────────────────── */

/** Best-effort write to web localStorage.  Native is a no-op. */
function persist(items: string[]): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* Quota / private-mode / disabled storage — silently ignore. */
  }
}

/** Best-effort read from web localStorage.  Returns [] on native or
 *  any failure.  Used by the boot-time hydrate thunk. */
export function readPersistedRecents(): string[] {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s): s is string => typeof s === 'string');
  } catch {
    return [];
  }
}
